import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from "../config/config";
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dispatchWebsiteNotification } from '../utils/browserNotifications';
import { applyCancellationBalances, cancellationRefundSummary } from '../utils/cancellationResult';

const CancelOrderDialog = ({ order, onConfirm, onClose }) => {
  const [reasons, setReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState(null); // Changed to store reason.id directly
  const [loading, setLoading] = useState(true);
  const [cancellationResult, setCancellationResult] = useState(null);
  const navigate = useNavigate();

  const apiUrl = API_URL;

  useEffect(() => {
    console.log('CancelOrderDialog: Fetching cancellation reasons for order:', order?._id);
    const fetchCancellationReasons = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('CancelOrderDialog: No token found');
        toast.error('Error: No token found. Please sign in.');
        navigate('/');
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/order/cancellation-reasons`, {
          params: { orderId: order?._id },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        const data = response.data;
        console.log('CancelOrderDialog: Fetched cancellation reasons:', data);
        if (!data.reasons || data.reasons.length === 0) {
          console.warn('CancelOrderDialog: No reasons returned from API');
          toast.error('No cancellation reasons available');
          setReasons([]);
        } else {
          setReasons(data.reasons);
        }
      } catch (error) {
        console.error('CancelOrderDialog: Error fetching cancellation reasons:', error);
        toast.error(error.response?.data?.message || 'Failed to fetch cancellation reasons');
        setReasons([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCancellationReasons();
  }, [apiUrl, navigate, order?._id]);

  const handleReasonChange = (reasonId) => {
    console.log('CancelOrderDialog: Selected reason ID:', reasonId);
    setSelectedReasonId(reasonId);
  };

  const handleConfirm = async () => {
    console.log('CancelOrderDialog: Confirm clicked, selectedReasonId:', selectedReasonId);
    if (!selectedReasonId) {
      console.warn('CancelOrderDialog: No reason selected');
      toast.error('Please select a cancellation reason');
      return;
    }

    const selectedReason = reasons.find((reason) => reason._id === selectedReasonId);
    if (!selectedReason) {
      console.error('CancelOrderDialog: Selected reason not found in reasons:', reasons);
      toast.error('Invalid cancellation reason selected');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('CancelOrderDialog: No token found');
      toast.error('Error: No token found. Please sign in.');
      navigate('/');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.patch(
        `${apiUrl}/order/cancel/${order._id}`,
        {
          reasonId: selectedReason._id,
          charges: selectedReason.charges,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('CancelOrderDialog: Cancel response:', response.data);
      if (response.status === 200 && !response.data?.error) {
        applyCancellationBalances(response.data);
        setCancellationResult(response.data);
        const refundedWallet = Number(response.data?.refunded_wallet || 0);
        toast.success('Order cancelled successfully.');
        dispatchWebsiteNotification({
          eventKey: `order:${order._id}:cancelled`,
          title: "Order Cancelled",
          body: `Your order #${order.orderId || order._id} has been cancelled.`,
          tag: `cancelled-${order._id}`,
        });
        if (refundedWallet > 0) {
          dispatchWebsiteNotification({
            eventKey: `payment:${order._id}:refund:${refundedWallet}`,
            title: "Payment Refunded",
            body: `₹${refundedWallet} has been refunded to your wallet for order #${order.orderId || order._id}.`,
            tag: `refund-${order._id}`,
          });
        }
        await Promise.all([
          axios.post(
            `${apiUrl}/notify/send-order-related?userId=${order.customer}&type=order_cancelled&orderId=${order.orderId}&appName=customerApp`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch((error) => console.error('CancelOrderDialog: Error sending customer notification:', error)),
          order.rider &&
            axios.post(
              `${apiUrl}/notify/send-order-related?userId=${order.rider.id}&type=order_cancelledr&orderId=${order.orderId}&appName=riderApp`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch((error) => console.error('CancelOrderDialog: Error sending rider notification:', error)),
        ]);
      } else {
        toast.error(response.data.message || 'Failed to cancel order');
      }
    } catch (error) {
      console.error('CancelOrderDialog: Error cancelling order:', error);
      toast.error(error.response?.data?.message || 'Network error cancelling order');
    } finally {
      setLoading(false);
    }
  };

  const handleReasonClick = (e, reasonId) => {
    e.stopPropagation(); // Prevent event bubbling
    handleReasonChange(reasonId);
  };

  const getChargesSubtitle = () => {
    if (!reasons.length) {
      return "No cancellation charges";
    }
    const selectedCharges = reasons.find((reason) => reason._id === selectedReasonId)?.charges;
    const maxCharges = reasons.reduce((max, item) => (item.charges > max ? item.charges : max), 0);
    const effectiveCharges = selectedCharges ?? maxCharges;
    if (effectiveCharges <= 0) {
      return "No cancellation charges will be deducted";
    }
    return `Cancellation charges ₹${Math.round(effectiveCharges)} deducted from your wallet`;
  };

  if (cancellationResult) {
    const finish = () => onConfirm(cancellationResult);
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 font-Poppins">
        <div role="dialog" aria-modal="true" aria-labelledby="cancellation-refund-title" className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
          <h2 id="cancellation-refund-title" className="text-xl font-bold mb-4">Cancellation refund</h2>
          <p className="whitespace-pre-line text-sm text-gray-700">{cancellationRefundSummary(cancellationResult)}</p>
          <div className="mt-6 flex justify-end gap-3">
            {Number(cancellationResult.refunded_wallet) > 0 && <button type="button" className="rounded-lg border px-4 py-2" onClick={() => { finish(); navigate('/wallet'); }}>View wallet &amp; receipt</button>}
            <button type="button" className="rounded-lg bg-yellow-400 px-4 py-2" onClick={finish}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 font-Poppins">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all duration-300">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Why do you want to cancel?</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors duration-200">
            <X size={28} />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-6">{getChargesSubtitle()}</p>
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#FFC107]"></div>
              <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-sm font-medium">
                Loading...
              </div>
            </div>
          </div>
        ) : reasons.length === 0 ? (
          <p className="text-center text-gray-600 font-medium">No cancellation reasons available</p>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {reasons.map((reason) => (
              <label
                key={reason._id}
                className={`flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${
                  selectedReasonId === reason._id ? 'bg-[#FFF8E1] border-[#FFC107]' : ''
                }`}
                onClick={(e) => handleReasonClick(e, reason._id)}
              >
                <input
                  type="radio"
                  name="cancellation-reason"
                  value={reason._id}
                  checked={selectedReasonId === reason._id}
                  onChange={() => handleReasonChange(reason._id)}
                  className="text-[#FFC107] focus:ring-[#FFC107] w-5 h-5"
                />
                <span className="text-sm text-gray-800 font-medium">{reason.reasonText}</span>
              </label>
            ))}
          </div>
        )}
        <div className="mt-8 flex justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 font-semibold hover:text-gray-900 transition-colors duration-200"
          >
            Back
          </button>
          <button
            onClick={handleConfirm}
            className={`px-6 py-2 bg-gradient-to-r from-[#FFC107] to-[#FFA000] text-white rounded-lg font-semibold hover:from-[#FFA000] hover:to-[#FFC107] transition-all duration-200 shadow-md ${
              !selectedReasonId || loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={!selectedReasonId || loading}
          >
            {loading ? 'Confirming...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelOrderDialog;
